import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// No-signup email relay: forwards each inquiry straight to Kristin's inbox
const FORM_RELAY = "https://formsubmit.co/ajax/kristin.mcintyre@exprealty.com";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { INTERESTS, KM } from "../../data/km";

const field = "w-full bg-transparent border-b border-black/20 py-3 text-base sm:text-sm outline-none focus:border-black transition-colors placeholder:text-black/40";

export default function ConnectDialog({ open, onOpenChange, interest }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", interest: "", message: "", consent: false });
  const [sent, setSent] = useState(false);
  React.useEffect(() => { if (open) { setSent(false); setF((p) => ({ ...p, interest: interest || p.interest })); } }, [open, interest]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Please add your name and a valid email.");
    if (!f.consent) return toast.error("Please agree to be contacted.");
    setSending(true);
    try {
      // 1) email to Kristin
      const res = await fetch(FORM_RELAY, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `New website inquiry from ${f.name}${f.interest ? ` - ${f.interest}` : ""}`,
          _template: "table",
          _captcha: "false",
          _replyto: f.email,
          Name: f.name,
          Email: f.email,
          Phone: f.phone || "-",
          Interest: f.interest || "-",
          Message: f.message || "-",
        }),
      });
      if (!res.ok) throw new Error("email relay failed");
      // 2) backup copy in the database (non-blocking)
      axios.post(`${API}/leads`, f).catch(() => {});
      setSent(true);
      toast.success("Thank you \u2014 Kristin will be in touch soon.");
      setF({ name: "", email: "", phone: "", interest: "", message: "", consent: false });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call 469-401-9817.");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[92svh] overflow-y-auto rounded-none p-6 sm:p-10 bg-white" data-testid="connect-dialog">
        <DialogTitle className="km-serif text-4xl font-normal">Let's <em>connect.</em></DialogTitle>
        <DialogDescription className="km-serif italic text-base text-black/60">{KM.motto}</DialogDescription>
        {sent ? (
          <div className="py-8" data-testid="connect-success">
            <p className="text-sm leading-relaxed mb-8">Your message is on its way. Expect a reply within one business day.</p>
            <button className="km-btn-dark" onClick={() => onOpenChange(false)}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-2" data-testid="connect-form">
            <input className={field} placeholder="Name" value={f.name} onChange={set("name")} data-testid="connect-name" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={field} placeholder="Email" value={f.email} onChange={set("email")} data-testid="connect-email" />
              <input className={field} placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="connect-phone" />
            </div>
            <Select value={f.interest} onValueChange={set("interest")}>
              <SelectTrigger className="rounded-none border-0 border-b border-black/20 px-0 shadow-none focus:ring-0 text-base sm:text-sm data-[placeholder]:text-black/40" data-testid="connect-interest"><SelectValue placeholder="I'm interested in..." /></SelectTrigger>
              <SelectContent>{INTERESTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
            <textarea rows={3} className={field} placeholder="Tell me a little about your home or your search" value={f.message} onChange={set("message")} data-testid="connect-message" />
            <label className="flex gap-3 items-start text-[11px] leading-relaxed text-black/60 cursor-pointer pt-2">
              <Checkbox checked={f.consent} onCheckedChange={set("consent")} data-testid="connect-consent" />
              <span>I agree to be contacted by {KM.name} via call, email, and text for real estate services. Reply STOP to opt out. Message and data rates may apply.</span>
            </label>
            <button type="submit" disabled={sending} className="km-btn-dark mt-4 disabled:opacity-60" data-testid="connect-submit">{sending ? "Sending..." : "Send Message"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
